import {
  InstantAnswerBuilderFn,
  InstantAnswerContext,
  InstantAnswerScenario,
} from "@faire/help-center-self-serve/types";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";

import { orderIsFreeDutiesEligible } from "../../utils/orderIsFreeDutiesEligible";

import { TAXES_AND_FEES_ARE_NOT_COVERED_BY_FAIRE } from "./shared";

export const DutiesInvoiceIA: InstantAnswerBuilderFn = (strictLocalize) => {
  return {
    titleBuilder: (context) => {
      return getAnswer(context, strictLocalize).getTitle();
    },

    descriptionBuilder: (context) => {
      return getAnswer(context, strictLocalize).getDescription();
    },
  };
};

const getAnswer = (
  context: InstantAnswerContext,
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => {
  const { brandOrder } = context;
  if (!brandOrder) {
    return ORDER_NOT_SELECTED(strictLocalize);
  }

  return orderIsFreeDutiesEligible(brandOrder)
    ? DUTIES_FREE_ORDER(strictLocalize)
    : NON_DUTIES_FREE_ORDER(strictLocalize);
};

const ORDER_NOT_SELECTED = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => DUTIES_INVOICES_WITH_FAIRE(strictLocalize),
  getDescription: () => [
    ORDERS_WITH_DUTIES_ELIGIBLE_GOODS_GENERATE_DUTIES_INVOICE(strictLocalize),
    YOU_WILL_BE_RESPONSIBLE_FOR_DUTIES(strictLocalize),
    TAXES_AND_FEES_ARE_NOT_COVERED_BY_FAIRE(strictLocalize),
  ],
});

const DUTIES_FREE_ORDER = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => DUTIES_INVOICES_WITH_FAIRE(strictLocalize),
  getDescription: () => [
    ORDER_ELIGIBLE_FOR_DUTIES_FREE_PROMOTION(strictLocalize),
    TAXES_AND_FEES_ARE_NOT_COVERED_BY_FAIRE(strictLocalize),
  ],
});

const NON_DUTIES_FREE_ORDER = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => DUTIES_INVOICES_WITH_FAIRE(strictLocalize),
  getDescription: () => [
    ORDER_NOT_ELIGIBLE_FOR_DUTIES_FREE_PROMOTION(strictLocalize),
    TAXES_AND_FEES_ARE_NOT_COVERED_BY_FAIRE(strictLocalize),
  ],
});

const DUTIES_INVOICES_WITH_FAIRE = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "Duties invoices with Faire",
    description: {
      text: "Title of a support answer shown to retailers",
    },
  });

const ORDERS_WITH_DUTIES_ELIGIBLE_GOODS_GENERATE_DUTIES_INVOICE = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "All orders that have duties eligible goods generate a duties invoice. If duties are covered for your order, we’ll apply a discount to your invoice at checkout that reflects the estimated cost of import duties for your order. The carrier will send you an invoice for all import fees (including import duties and VAT) when the order ships, which you’ll pay in full. If the duties are covered and actual cost of import duties and disbursement fees is greater than the duties promotion you received, you can request a refund in Faire credit for the difference when the order is delivered.",
    description: {
      text: "Part of the support answer shown to retailers explaining how duties work with Faire orders",
    },
  });

const YOU_WILL_BE_RESPONSIBLE_FOR_DUTIES = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "If duties are not covered for your order, you will be responsible for duties fees.",
    description: {
      text: "Part of the support answer shown to retailers explaining how duties work with Faire orders",
    },
  });

const ORDER_ELIGIBLE_FOR_DUTIES_FREE_PROMOTION = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "This order is eligible for free duties promotion. All orders that have duties eligible goods generate a duties invoice. Since duties are covered for your order, we’ll apply a discount to your invoice at checkout that reflects the estimated cost of import duties for your order. The carrier will send you an invoice for all import fees (including import duties and VAT) when the order ships, which you’ll pay in full. If the actual cost of import duties and disbursement fees is greater than the duties promotion you received, you can request a refund in Faire credit for the difference when the order is delivered.",
    description: {
      text: "Part of the support answer shown to retailers in relation to an order that is eligible for free duties",
    },
  });

const ORDER_NOT_ELIGIBLE_FOR_DUTIES_FREE_PROMOTION = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "This order is not eligible for free duties promotion. All orders that have duties eligible goods generate a duties invoice. Since duties are not covered for your order, you will be responsible for duties fees. The carrier will send you an invoice for all import fees (including import duties and VAT) when the order ships, which you’ll pay in full.",
    description: {
      text: "Part of the support answer shown to retailers in relation to an order that is not eligible for free duties",
    },
  });
